import { getProductData } from "../../utilities";

const Variants = {
  /**
   * @typedef {Object} AttrsToMatch
   * @property {HTMLInputElement} [attr1]
   * @property {HTMLInputElement} [attr2]
   * @property {HTMLInputElement} [attr3]
   * 
   * @param {AttrsToMatch} attrsToMatch
  */

  // Auxiliar. Serve para validar se uma variante dá match com os atributos necessários
  matchVariant: function(variant, attrsToMatch) {
    if (!variant || !attrsToMatch) return null

    // Salva as properties desta variante para uso
    const property1 = variant.properties?.property1 || false
    const property2 = variant.properties?.property2 || false
    const property3 = variant.properties?.property3 || false

    // Prepara validação dos três atributos. Se algum atributo não existe no objeto
    // dos atributos a serem validados, desconsidera este atributo
    let attr1match = !attrsToMatch.attr1 ? true : false;
    let attr2match = !attrsToMatch.attr2 ? true : false;
    let attr3match = !attrsToMatch.attr3 ? true : false;

    // Verifica se algum dos atributos é inexistente no cadastro da variante
    if (property1 == null || property1?.value == null || property1?.value == '') attr1match = true
    if (property2 == null || property2?.value == null || property2?.value == '') attr2match = true
    if (property3 == null || property3?.value == null || property3?.value == '') attr3match = true

    // Verifica se a variante possui os atributos selecionados
    if (!attr1match && property1.value == attrsToMatch.attr1?.value) {
      attr1match = true
    }

    if (!attr2match && property2.value == attrsToMatch.attr2?.value) {
      attr2match = true
    }

    if (!attr3match && property3.value == attrsToMatch.attr3?.value) {
      attr3match = true
    }

    return attr1match && attr2match && attr3match ? variant : false
  },

  // Retorna variante com SKU válido, com base nos atributos selecionados em tela
  findSelectedVariant: async function (form) {
    const id = form.getAttribute('data-product-id');
    if (!id) return null
    
    const productData = await getProductData(id);
    if (!productData.success) return null

    const { variants } = productData.data
    if (!variants) return null

    const selectedAttrs = {
      attr1: form.querySelector('[data-attribute="1"] input:checked'),
      attr2: form.querySelector('[data-attribute="2"] input:checked'),
      attr3: form.querySelector('[data-attribute="3"] input:checked'),
    };

    // Busca variante disponível com base nos atributos selecionados
    for (let index = 0; index < variants.length; index++) {
      const variant = variants[index];

      const matchedVariant = Variants.matchVariant(variant, selectedAttrs)
      if (matchedVariant) return variant
    }

    // Quando não encontra variante compatível
    return null;
  },

  // Lida com as classes de exibição das labels dos atributos
  handleLabelDisplay: async function (form, displayOrder, changedInput = false) {
    if (!displayOrder) {
      // considerar disparar erro e travar formulário
      console.error('attrDisplayOrder foi removido. Adicionar novamente no arquivo form.js.\nImpossível prosseguir, labels não serão remarcadas.')
      return
    }

    const id = form.getAttribute('data-product-id');
    if (!id) return
    
    const productData = await getProductData(id);
    if (!productData.success) return

    const { variants } = productData.data
    if (!variants) return

    // Agrega todas as opções de atributos exibidas em tela
    const allInputs = {
      attr1: [...form.querySelectorAll('[data-attribute="1"] input')],
      attr2: [...form.querySelectorAll('[data-attribute="2"] input')],
      attr3: [...form.querySelectorAll('[data-attribute="3"] input')],
    };

    // Agrega as opções dos atributos selecionados
    const selectedInputs = {
      attr1: form.querySelector('[data-attribute="1"] input:checked'),
      attr2: form.querySelector('[data-attribute="2"] input:checked'),
      attr3: form.querySelector('[data-attribute="3"] input:checked'),
    };
    
    // Atualiza as classes de exibição das labels,
    // seguindo a ordem de prioridade definida no form.js
    for (const [index, order] of displayOrder.entries()) {
      for (let i = index + 1; i < displayOrder.length; i++) {
        const attrsToCheck = displayOrder[i]
        Variants.markLabels(order, allInputs[`attr${attrsToCheck}`], attrsToCheck, variants, selectedInputs, form, displayOrder);
      }
    }

    // Lida com possível label disabled que ficou selecionada
    Variants.handleDisabledLabels(selectedInputs, form, displayOrder, changedInput)
  },

  // Define as labels como available, unavailable e disabled
  // com base nas opções disponíveis de variantes do produto
  markLabels: function(currentNumber, inputs, checkingNumber, variants, selectedInputs, form, displayOrder) {
    if (inputs == null || inputs.length == 0) {
      console.log(`markLabels: Não há labels/opções para o atributo ${currentNumber}`)
      return
    }

    // Encontra variantes válidas para combinação de atributo na base da ordem de prioridade
    // Exemplo: se for [1, 2, 3], valida todos os attr2 perante o attr1 selecionado
    inputs.forEach(input => {
      let attrsToMatch = {}

      // Index da ordem atual de exibição deste atributo
      const thisOrderIndex = displayOrder.findIndex(order => order == currentNumber);

      // Alimenta os atributos dentro do obj se o atributo é anterior (na ordem) ao attr sendo verificado
      displayOrder.forEach((order, index) => {
        if (index <= thisOrderIndex){
          attrsToMatch[`attr${order}`] = selectedInputs[`attr${order}`]
        }
      })

      // coloca o input atual no objeto de atributos a serem validados
      attrsToMatch[`attr${checkingNumber}`] = input

      // Encontra variantes válidas para o grupo de atributos
      const validVariants = variants.filter(variant => {
        const matchedVariant = Variants.matchVariant(variant, attrsToMatch)
        if (matchedVariant) return variant
      })

      // Reseta status da label
      const label = form.querySelector(`[data-attribute-value="${input.value}"]`)
      label.classList.remove('-available');
      label.classList.remove('-unavailable');
      label.classList.remove('-disabled');

      // Não encontrou variante válida para este atributo
      if (validVariants.length === 0) {
        label.classList.add('-disabled')
        return
      }

      // Procura por variante disponível dentre das variantes válidas
      let available = false
      for (const variant of validVariants) {
        const property = variant.properties[`property${checkingNumber}`]
        if (!variant.available || variant.stock == 0 || !property) continue

        if (property.value == input.value) {
          available = true
          break
        }
      }

      return available ? label.classList.add('-available') : label.classList.add('-unavailable');
    })    
  },

  // Lida com as labels que ficaram como disabled. Se ela era a selecionada anteriormente,
  // muda para a primeira label available disponível no mesmo grupo de atributos
  handleDisabledLabels: function (selectedInputs, form, displayOrder, changedInput) {
    if (!selectedInputs) return

    for (const input in selectedInputs) {
      const inputEl = selectedInputs[input]
      if (!inputEl) continue

      const label = form.querySelector(`[data-attribute-value="${inputEl.value}"]`)
      const disabled = label.classList.contains('-disabled')
      const unavailable = label.classList.contains('-unavailable')

      const attrIndex = label.getAttribute('data-attribute-index')

      const firstAvailable = form.querySelector(`[data-attribute-index="${attrIndex}"].-available`)

      // Labels disabled sempre mudam (combinação impossível)
      if (disabled) {

        if (firstAvailable) {
          return firstAvailable.click()
        }

        const firstUnavailable = form.querySelector(`[data-attribute-index="${attrIndex}"].-unavailable`)
        if (firstUnavailable) {
          return firstUnavailable.click()
        }

        const allInputs = form.querySelectorAll(`[data-attribute="${attrIndex}"] input`)
        return allInputs.forEach(input => { input.checked = false })
      }

      // Quando um atributo prioritário é alterado, a mudança para o primeiro atributo disponível é feita automaticamente
      // Quando o usuário clicar no atributo unavailable propositalmente, mantemos sua seleção (viabiliza o uso do avise-me)
      let automaticChange = false
      if (changedInput) {
        // Identifica qual atributo foi alterado (1, 2 ou 3)
        const changedAttributeNumber = parseInt(changedInput.closest('[data-attribute]').getAttribute('data-attribute'))

        // Verifica se o atributo alterado é o primeiro na hierarquia de prioridade
        // Ex: se displayOrder = [1, 2, 3], só o atributo 1 força mudança automática
        const isHighestPriorityAttribute = displayOrder.indexOf(changedAttributeNumber) === 0

        // Só força mudança automática se o atributo alterado está disponível
        // Se usuário clicou propositalmente em atributo indisponível, mantém sua seleção
        const changedInputLabel = form.querySelector(`[data-attribute-value="${changedInput.value}"]`)
        const changedInputIsAvailable = changedInputLabel && changedInputLabel.classList.contains('-available')

        automaticChange = isHighestPriorityAttribute && changedInputIsAvailable
      }

      if (unavailable && automaticChange && firstAvailable) {
        return firstAvailable.click()
      }
    }
  }
}

export default Variants;
